import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, Grid, MenuItem, TextField, CircularProgress } from '@mui/material';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import TableService from 'services/TableService';
import { AlertMessagesContext } from 'react-alert-messages';
import NumberField from 'components/library/NumberField';
import { InventoryContext } from 'Context/InventoryContext';
import { ShopContext } from 'Context/ShopContext';
import { SUBSCRIPTION_TYPE } from 'const';

export default function AreaFormDialog({ area = {}, handleClose, handleSuccess }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');

  const { shop } = useContext(ShopContext);
  const { priceCategories } = useContext(InventoryContext);
  const subscriptionType = shop?.subscription?.type;
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState({
    ...area,
    price_category_id: area?.price_category ? area?.price_category?.id : null,
  });

  const { postAlertMessage } = useContext(AlertMessagesContext);

  const handleActionButton = async () => {
    setLoading(true);
    try {
      if (data.id) {
        await TableService.updateArea(data.id, data);
        postAlertMessage({ text: tPopup('Area updated successfully'), type: 'success' });
      } else {
        await TableService.createArea(data);
        postAlertMessage({ text: tPopup('Area created successfully'), type: 'success' });
      }
      await handleSuccess();
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
      handleClose();
    }
    setLoading(false);
  };

  return (
    <>
      <div className={styles.popUp}>
        <div className={styles.contentWrapper}>
          <div className={styles.headSec}>
            <h2 className={styles.editTitle}>{tCommon('Area Details')}</h2>
            <CloseIcon onClick={handleClose} style={{ cursor: 'pointer' }} />
          </div>
          <div className={styles.inputSec}>
            <div>
              <Grid container alignItems="center">
                <Grid item sm={3} xs={3}>
                  <h6 className={styles.tableName}>{tCommon('Area Name')}</h6>
                </Grid>
                <Grid item sm={9} xs={9}>
                  <TextField
                    label={tCommon('Area Name')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="text"
                    value={data?.name}
                    onChange={(e) => {
                      setData({ ...data, name: e.target.value });
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container alignItems="center" sx={{ marginTop: 2 }}>
                <Grid item sm={3} xs={3}>
                  <h6 className={styles.tableName}>{tCommon('Order')}</h6>
                </Grid>
                <Grid item sm={9} xs={9}>
                  <NumberField
                    label={tCommon('Order')}
                    value={data?.order}
                    onChange={(e) => {
                      setData({ ...data, order: Number(e.target.value) });
                    }}
                  />
                </Grid>
              </Grid>
              {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.PREMIUM && (
                <Grid container alignItems="center" sx={{ marginTop: 2 }}>
                  <Grid item sm={3} xs={3}>
                    <h6 className={styles.tableName}>{tCommon('Price Category')}</h6>
                  </Grid>
                  <Grid item sm={9} xs={9}>
                    <TextField
                      select
                      label={tCommon('Price Category')}
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                      value={data?.price_category_id}
                      onChange={(e) => {
                        setData({ ...data, price_category_id: e.target.value });
                      }}
                    >
                      <MenuItem value={null}>None</MenuItem>
                      {priceCategories?.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              )}
              <Grid container alignItems="center" sx={{ marginTop: 2 }}>
                <Grid item sm={3} xs={3}>
                  <h6 className={styles.tableName}>{tCommon('Invoice Printer IP')}</h6>
                </Grid>
                <Grid item sm={9} xs={9}>
                  <TextField
                    select
                    label={tCommon('Invoice Printer IP')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data?.invoice_printer_ip || ''}
                    onChange={(e) => {
                      setData({
                        ...data,
                        invoice_printer_ip: e.target.value,
                      });
                    }}
                  >
                    <MenuItem value="">{tCommon('--')}</MenuItem>
                    {shop?.config?.ip_printers?.map((printer) => (
                      <MenuItem key={printer.ip_address} value={printer.ip_address}>
                        {printer.name} ({printer.ip_address})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </div>
          </div>
          <div className={styles.footSec}>
            <DialogActions>
              <Button variant="contained" color="secondary" size="small" onClick={handleClose}>
                {tCommon('Close')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ backgroundColor: '#00a65a' }}
                disabled={!data.name || isLoading}
                onClick={handleActionButton}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    {data.id ? tCommon('Updating...') : tCommon('Creating...')}
                  </>
                ) : data.id ? (
                  tCommon('Update Area')
                ) : (
                  tCommon('Create Area')
                )}
              </Button>
            </DialogActions>
          </div>
        </div>
      </div>
    </>
  );
}
